/**
 * Variant Carousel Media Hiding Sync
 * 
 * This component synchronizes the variant carousel with the "hide unselected variant media" setting.
 * When this setting is enabled, only carousel items corresponding to the selected variant are visible.
 * 
 * Features:
 * - Filters carousel items when variant changes
 * - Syncs with main product gallery hiding logic
 * - Updates carousel navigation to skip hidden items
 * - Maintains proper aria-labels for accessibility
 * - Handles both initial page load and dynamic variant changes
 */

class VariantCarouselMediaHidingSync {
  constructor() {
    this.initialized = false;
    this.carouselCleanupMap = new WeakMap(); // Track cleanup functions for each carousel
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find all variant carousels with hide-unselected-media attribute
    const carousels = document.querySelectorAll('variant-image-carousel[hide-unselected-media]');
    
    carousels.forEach(carousel => {
      this.setupCarouselSync(carousel);
    });

    // Also setup on section reload (Shopify theme editor)
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        const carousels = document.querySelectorAll('variant-image-carousel[hide-unselected-media]');
        carousels.forEach(carousel => {
          this.setupCarouselSync(carousel);
        });
      });
    }

    this.initialized = true;
    console.log('VariantCarouselMediaHidingSync: Initialized');
  }

  setupCarouselSync(carousel) {
    if (!carousel) return;

    // Clean up existing listeners if this carousel was already set up
    this.cleanupCarousel(carousel);

    const optionIndex = parseInt(carousel.dataset.optionIndex || '0');
    const productId = carousel.dataset.productId;

    // Find the parent variant picker
    const variantPicker = carousel.closest('variant-picker');
    if (!variantPicker) {
      console.warn('VariantCarouselMediaHidingSync: Could not find parent variant-picker');
      return;
    }

    const formId = variantPicker.getAttribute('form-id');
    if (!formId) return;

    const form = document.forms[formId];
    if (!form) return;

    // Store references for cleanup
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Listen for variant changes and update carousel filtering
    const variantChangeHandler = (event) => {
      this.onVariantChange(carousel, event, optionIndex);
    };
    form.addEventListener('variant:change', variantChangeHandler, { signal });

    // Listen for option changes within the same fieldset
    const optionFieldset = carousel.closest('fieldset.variant-picker__option');
    if (optionFieldset) {
      const radioInputs = optionFieldset.querySelectorAll('input[type="radio"]');
      radioInputs.forEach(input => {
        const optionChangeHandler = () => {
          // Small delay to ensure variant:change has fired
          setTimeout(() => {
            this.updateCarouselFromSelection(carousel, optionIndex, form);
          }, 50);
        };
        input.addEventListener('change', optionChangeHandler, { signal });
      });
    }

    // Store cleanup function
    this.carouselCleanupMap.set(carousel, {
      abortController
    });

    console.log('VariantCarouselMediaHidingSync: Setup complete for carousel');
  }

  /**
   * Clean up event listeners for a carousel
   */
  cleanupCarousel(carousel) {
    if (!carousel) return;

    const cleanup = this.carouselCleanupMap.get(carousel);
    if (!cleanup) return;

    // Abort all event listeners
    if (cleanup.abortController) {
      cleanup.abortController.abort();
    }

    this.carouselCleanupMap.delete(carousel);
    console.log('VariantCarouselMediaHidingSync: Cleaned up carousel');
  }

  /**
   * Handle variant change event
   */
  onVariantChange(carousel, event, optionIndex) {
    if (!event.detail.variant) return;

    const variant = event.detail.variant;
    const selectedOptionValue = variant.options[optionIndex];

    if (!selectedOptionValue) return;

    // Get all carousel items
    const items = Array.from(carousel.querySelectorAll('.variant-carousel__item'));
    if (items.length === 0) return;

    // Build list of indexes to hide (items that don't match selected option)
    const indexesToHide = [];
    items.forEach((item, index) => {
      const itemOptionText = item.dataset.optionText;
      const itemColor = item.dataset.color;

      // Check if this item matches the selected option value
      const matches = 
        itemOptionText === selectedOptionValue ||
        itemOptionText?.toLowerCase() === selectedOptionValue.toLowerCase() ||
        itemColor === selectedOptionValue.toLowerCase();

      if (!matches) {
        indexesToHide.push(index);
      }
    });

    // Apply filtering to carousel
    if (typeof carousel.filterItems === 'function') {
      carousel.filterItems(indexesToHide);
    } else {
      // Fallback: manually hide items if filterItems method not available
      items.forEach((item, index) => {
        item.toggleAttribute('hidden', indexesToHide.includes(index));
      });
    }

    console.log(`VariantCarouselMediaHidingSync: Filtered carousel for option "${selectedOptionValue}" (hiding ${indexesToHide.length} items)`);
  }

  /**
   * Update carousel filtering based on current selection
   */
  updateCarouselFromSelection(carousel, optionIndex, form) {
    // Get the variant picker (it's a sibling of the form, not a child)
    const variantPicker = carousel.closest('variant-picker');
    if (!variantPicker) return;

    const variantDataElement = variantPicker.querySelector('[data-variant]');
    if (!variantDataElement) return;

    try {
      const currentVariant = JSON.parse(variantDataElement.textContent);
      if (currentVariant && currentVariant.options) {
        this.onVariantChange(carousel, { detail: { variant: currentVariant } }, optionIndex);
      }
    } catch (e) {
      console.error('VariantCarouselMediaHidingSync: Failed to parse variant data', e);
    }
  }
}

// Initialize the sync on page load
if (!window.variantCarouselMediaHidingSync) {
  window.variantCarouselMediaHidingSync = new VariantCarouselMediaHidingSync();
}
